import { defineComponent as _defineComponent } from 'vue'
import type { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';

import NewDesignModal from '@/components/modals/vfm/NewDesignModal.vue';
import { computed, ref } from 'vue';

import type { TrailerClip } from '@/interfaces/trailerClip';
import { getTrailerComponent } from '@/helpers/trailer-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import { isProWebCountry } from '@/helpers/pro-web-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { usePrimePlayBtnExp } from '@/components/experiments/ApplePlayBtnExp';


export default /*#__PURE__*/_defineComponent({
  __name: 'ApplePlayBtnExp',
  props: {
    clips: null,
    title: null,
    source: { default: 'modal' },
    trackingProperty: null,
    additionalContexts: { default: () => [] },
    playerAdditionalContexts: null,
    hideTrailerAd: { type: Boolean }
  },
  emits: ['dismiss', 'close'],
  setup(__props: any) {

const props = __props as { clips: MaybeRefOrGetter<TrailerClip[]>, title: TitleDetail, source: string, trackingProperty?: string, additionalContexts: SnowplowContext[], playerAdditionalContexts?: SnowplowContext[], hideTrailerAd?: boolean };

const variantComponents = {
	variant_1: () => import('@/components/experiments/ApplePlayBtnExp/components/ApplePlayBtnExpV1.vue'),
	variant_2: () => import('@/components/experiments/ApplePlayBtnExp/components/ApplePlayBtnExpV2.vue'),
	variant_3: () => import('@/components/experiments/ApplePlayBtnExp/components/ApplePlayBtnExpV3.vue'),
	variant_4: () => import('@/components/experiments/ApplePlayBtnExp/components/ApplePlayBtnExpV4.vue'),
};

const JWTVProFooter = () => import('@/pages/titleDetailV1/v1/hero/JWTVProFooter.vue');





const options: YoutubePlayerOptions = { autoplay: 1, mute: 1 };

const { variant, experimentStrings, trackEvent } = usePrimePlayBtnExp();

function onImpressionVisible(isVisible: boolean) {
	if (isVisible) {
		trackEvent({ action: 'impression', label: 'trailer' });
	}
}

const variantComponent = computed(() =>
	variant.value ? variantComponents[variant.value as keyof typeof variantComponents] : null
);

const { isPremium } = storeToRefs(useUserStore());
const { titleName, titleObjectType } = useTitleDetailInfo(() => props.title);

const titleYear = computed(() => props.title?.content?.originalReleaseYear);
const { webLocale } = storeToRefs(useLanguageStore());

const showTrailer = ref(false);

const trailerClip = computed(() => toValue(props.clips)[0]);

const similarTitleName = computed(() => {
	if (showTrailer.value || !['variant_3', 'variant_4'].includes(variant.value || '')) return null;
	return experimentStrings.similarToLabel(props.title.content.title);
});

const showJWTVProFooter = computed(() => {
	return !isPremium.value && isProWebCountry(webLocale.value) && trailerClip.value?.provider === 'JUSTWATCH_TV';
});

const isYoutubePlayer = computed(() => trailerClip.value?.provider === 'YOUTUBE');

const videoPlayer = computed(() => getTrailerComponent(trailerClip.value?.provider));

function onTrack(eventName: string) {
	if (trailerClip.value?.provider !== 'YOUTUBE') return;

	const trackingMap = {
		buffering: 'youtube_buffering',
		playing: 'youtube_playing',
		paused: 'youtube_paused',
		ended: 'youtube_ended',
	};
	const category = trackingMap[eventName as keyof typeof trackingMap];
	if (category) {
		TrackingHelper.trackEvent(
			category,
			{
				action: titleObjectType.value.toLowerCase(),
				label: props.source,
				property: props.trackingProperty,
				nonInteraction: true,
				value: 1,
			},
			props.additionalContexts
		);
	}
}

function handleSkip() {
	showTrailer.value = true;
	trackEvent({ action: 'click', label: 'skip_native_ad' });
}

return { __sfc: true,variantComponents, JWTVProFooter, props, options, variant, experimentStrings, trackEvent, onImpressionVisible, variantComponent, isPremium, titleName, titleObjectType, titleYear, webLocale, showTrailer, trailerClip, similarTitleName, showJWTVProFooter, isYoutubePlayer, videoPlayer, onTrack, handleSkip, NewDesignModal }
}

})